import type { MonthAndYearProps } from '@eventbrite/timed-entry';
import type {
    AccordionSection,
    CalendarType,
    CompactCalendarData,
    ParentEventInfo,
    ScrollCalendarData,
    TimeSlot,
} from '../state/infiniteCalendarSeries';

export enum InfiniteCalendarSeriesActionType {
    setActiveSection = 'setActiveSection',
    setParentEventInfo = 'setParentEventInfo',
    setDateSectionLoading = 'setDateSectionLoading',
    setTimeSectionLoading = 'setTimeSectionLoading',
    setTicketSectionLoading = 'setTicketSectionLoading',
    setShouldShowTimeSection = 'setShouldShowTimeSection',
    setSelectedDate = 'setSelectedDate',
    setSelectedTime = 'setSelectedTime',
    setSelectedSessionId = 'setSelectedSessionId',
    setShouldShowTimeError = 'setShouldShowTimeError',
    setShouldShowTicketError = 'setShouldShowTicketError',
    setCalendarType = 'setCalendarType',
    setScrollCalendarData = 'setScrollCalendarData',
    setCompactCalendarData = 'setCompactCalendarData',
    setTimeSlots = 'setTimeSlots',
}

export interface SetSectionAction {
    type: InfiniteCalendarSeriesActionType.setActiveSection;
    payload: AccordionSection | null;
}

export interface SetParentEventInfoAction {
    type: InfiniteCalendarSeriesActionType.setParentEventInfo;
    payload: ParentEventInfo;
}

export interface SetDateSectionLoadingAction {
    type: InfiniteCalendarSeriesActionType.setDateSectionLoading;
    payload: boolean;
}

export interface SetTimeSectionLoadingAction {
    type: InfiniteCalendarSeriesActionType.setTimeSectionLoading;
    payload: boolean;
}

export interface SetTicketSectionLoadingAction {
    type: InfiniteCalendarSeriesActionType.setTicketSectionLoading;
    payload: boolean;
}

export interface SetShouldShowTimeSectionAction {
    type: InfiniteCalendarSeriesActionType.setShouldShowTimeSection;
    payload: boolean;
}

export interface SetShouldShowTimeErrorAction {
    type: InfiniteCalendarSeriesActionType.setShouldShowTimeError;
    payload: boolean;
}

export interface SetShouldShowTicketErrorAction {
    type: InfiniteCalendarSeriesActionType.setShouldShowTicketError;
    payload: boolean;
}

export interface SetSelectedDateAction {
    type: InfiniteCalendarSeriesActionType.setSelectedDate;
    payload: string;
}

export interface SetSelectedTimeAction {
    type: InfiniteCalendarSeriesActionType.setSelectedTime;
    payload: string;
}

export interface SetScrollCalendarDataAction {
    type: InfiniteCalendarSeriesActionType.setScrollCalendarData;
    payload: ScrollCalendarData;
}

export interface SetCompactCalendarDataAction {
    type: InfiniteCalendarSeriesActionType.setCompactCalendarData;
    payload: CompactCalendarData;
}

export interface SetSelectedSessionIdAction {
    type: InfiniteCalendarSeriesActionType.setSelectedSessionId;
    payload: string;
}

export interface SetCalendarTypeAction {
    type: InfiniteCalendarSeriesActionType.setCalendarType;
    payload: CalendarType;
}

export interface SetTimeSlotsAction {
    type: InfiniteCalendarSeriesActionType.setTimeSlots;
    payload: TimeSlot[];
}

export interface SetParentEventInfoAction {
    type: InfiniteCalendarSeriesActionType.setParentEventInfo;
    payload: ParentEventInfo;
}

export type InfiniteCalendarSeriesAction =
    | SetSectionAction
    | SetParentEventInfoAction
    | SetDateSectionLoadingAction
    | SetTimeSectionLoadingAction
    | SetTicketSectionLoadingAction
    | SetShouldShowTimeSectionAction
    | SetShouldShowTimeErrorAction
    | SetShouldShowTicketErrorAction
    | SetSelectedDateAction
    | SetSelectedTimeAction
    | SetSelectedSessionIdAction
    | SetCalendarTypeAction
    | SetScrollCalendarDataAction
    | SetCompactCalendarDataAction
    | SetTimeSlotsAction
    | SetParentEventInfoAction;

export interface InfiniteCalendarSeriesDispatcher {
    onDateSelect: (dateString: string) => void;
    onTimeSelect: (sessionId: string, sessionLabel: string) => void;
    onMonthLoad: (monthAndYear: MonthAndYearProps, timezone: string) => void;
    onToggleSection: (params: {
        activeSection: AccordionSection;
        selectedDate?: string;
        selectedTime?: string;
    }) => void;
}

export const setSection = (
    accordionName: AccordionSection | null,
): SetSectionAction => ({
    type: InfiniteCalendarSeriesActionType.setActiveSection,
    payload: accordionName,
});

export const setParentEventInfo = (parentEventInfo: ParentEventInfo) => ({
    type: InfiniteCalendarSeriesActionType.setParentEventInfo,
    payload: parentEventInfo,
});

export const setDateSectionLoading = (isLoading: boolean) => ({
    type: InfiniteCalendarSeriesActionType.setDateSectionLoading,
    payload: isLoading,
});

export const setTimeSectionLoading = (isLoading: boolean) => ({
    type: InfiniteCalendarSeriesActionType.setTimeSectionLoading,
    payload: isLoading,
});

export const setTicketSectionLoading = (isLoading: boolean) => ({
    type: InfiniteCalendarSeriesActionType.setTicketSectionLoading,
    payload: isLoading,
});

export const setShouldShowTimeSection = (shouldShow: boolean) => ({
    type: InfiniteCalendarSeriesActionType.setTicketSectionLoading,
    payload: shouldShow,
});

export const setShouldShowTimeError = (shouldShowError: boolean) => ({
    type: InfiniteCalendarSeriesActionType.setShouldShowTimeError,
    payload: shouldShowError,
});

export const setShouldShowTicketError = (shouldShowError: boolean) => ({
    type: InfiniteCalendarSeriesActionType.setShouldShowTicketError,
    payload: shouldShowError,
});

export const setSelectedDate = (selectedDate: string) => ({
    type: InfiniteCalendarSeriesActionType.setSelectedDate,
    payload: selectedDate,
});

export const setSelectedTime = (selectedTime: string) => ({
    type: InfiniteCalendarSeriesActionType.setSelectedTime,
    payload: selectedTime,
});

export const setScrollCalendarData = (calendarData: ScrollCalendarData) => ({
    type: InfiniteCalendarSeriesActionType.setScrollCalendarData,
    payload: calendarData,
});

export const setCompactCalendarData = (calendarData: CompactCalendarData) => ({
    type: InfiniteCalendarSeriesActionType.setCompactCalendarData,
    payload: calendarData,
});

export const setSelectedSessionId = (selectedSessionId: string) => ({
    type: InfiniteCalendarSeriesActionType.setSelectedSessionId,
    payload: selectedSessionId,
});

export const setCalendarType = (calendarType: CalendarType) => ({
    type: InfiniteCalendarSeriesActionType.setCalendarType,
    payload: calendarType,
});

export const setTimeSlots = (timeSlots: TimeSlot[]) => ({
    type: InfiniteCalendarSeriesActionType.setTimeSlots,
    payload: timeSlots,
});
